import {
  Avatar,
  Box,
  Button,
  Chip,
  Grid,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { MdBusiness, MdLockOutline, MdMailOutline } from "react-icons/md";
import { useNavigate } from "react-router-dom";

import PageLayout from "components/layout/PageLayout";
import { ROUTES } from "constants/index";
import {
  getUserDisplayName,
  getUserInitials,
  getUserRoleLabel,
} from "helpers/userPresentation";
import { useSession } from "providers/SessionProvider";

export default function UserSettingsPage() {
  const { userData } = useSession();
  const navigate = useNavigate();

  if (!userData) {
    return (
      <PageLayout
        pageTitle="Mi Perfil"
        pageDesc="Consultá tus datos de cuenta y administrá tu acceso."
      >
        <Paper
          variant="outlined"
          sx={{ p: { xs: 2, sm: 3 }, mt: 2, borderRadius: 3 }}
        >
          <Typography color="text.secondary">
            No se pudo cargar la información de tu perfil.
          </Typography>
        </Paper>
      </PageLayout>
    );
  }

  const displayName = getUserDisplayName(userData);
  const initials = getUserInitials(userData);
  const roleLabel = getUserRoleLabel(userData.role);

  return (
    <PageLayout
      pageTitle="Mi Perfil"
      pageDesc="Consultá tus datos de cuenta y administrá tu acceso."
    >
      <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ mt: 0 }}>
        <Grid item xs={12}>
          <Paper
            elevation={2}
            sx={{
              p: { xs: 2, sm: 3, md: 4 },
              borderRadius: 3,
              background: (theme) =>
                `linear-gradient(135deg, ${theme.palette.background.paper}, ${theme.palette.action.hover})`,
            }}
          >
            <Stack
              direction="row"
              spacing={{ xs: 1.5, sm: 3 }}
              alignItems={{ xs: "flex-start", sm: "center" }}
            >
              <Avatar
                aria-label={`Avatar de ${displayName}`}
                sx={{
                  width: { xs: 64, sm: 76 },
                  height: { xs: 64, sm: 76 },
                  bgcolor: "primary.main",
                  fontSize: { xs: "1.5rem", sm: "1.75rem" },
                  fontWeight: 600,
                }}
              >
                {initials}
              </Avatar>
              <Box>
                <Typography variant="h5" fontWeight={700} gutterBottom>
                  {displayName}
                </Typography>
                <Stack direction="row" flexWrap="wrap" gap={1}>
                  {typeof userData.is_active === "boolean" && (
                    <Chip
                      label={
                        userData.is_active ? "Cuenta activa" : "Cuenta inactiva"
                      }
                      color={userData.is_active ? "success" : "default"}
                      size="small"
                    />
                  )}
                  {roleLabel && (
                    <Chip
                      label={roleLabel}
                      color="primary"
                      variant="outlined"
                      size="small"
                    />
                  )}
                </Stack>
              </Box>
            </Stack>
          </Paper>
        </Grid>

        <Grid item xs={12} md={7}>
          <Paper
            elevation={2}
            sx={{ p: { xs: 2, sm: 3, md: 4 }, borderRadius: 3, height: "100%" }}
          >
            <Typography variant="h6" fontWeight={600} gutterBottom>
              Información de la cuenta
            </Typography>
            <Stack spacing={{ xs: 2, sm: 3 }} sx={{ mt: { xs: 2, sm: 3 } }}>
              {userData.email && (
                <Stack direction="row" spacing={2} alignItems="flex-start">
                  <MdMailOutline size={24} aria-hidden="true" />
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" color="text.secondary">
                      Correo electrónico
                    </Typography>
                    <Typography sx={{ overflowWrap: "anywhere" }}>
                      {userData.email}
                    </Typography>
                  </Box>
                </Stack>
              )}
              {userData.client?.razon_social && (
                <Stack direction="row" spacing={2} alignItems="flex-start">
                  <MdBusiness size={24} aria-hidden="true" />
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" color="text.secondary">
                      Cliente asociado
                    </Typography>
                    <Typography>{userData.client.razon_social}</Typography>
                  </Box>
                </Stack>
              )}
            </Stack>
          </Paper>
        </Grid>

        <Grid item xs={12} md={5}>
          <Paper
            elevation={2}
            sx={{ p: { xs: 2, sm: 3, md: 4 }, borderRadius: 3, height: "100%" }}
          >
            <Stack spacing={{ xs: 1.5, sm: 2 }} alignItems="flex-start">
              <Stack
                direction="row"
                spacing={{ xs: 1.5, sm: 2 }}
                alignItems="center"
              >
                <Avatar
                  sx={{
                    bgcolor: "secondary.main",
                    width: { xs: 36, sm: 40 },
                    height: { xs: 36, sm: 40 },
                  }}
                >
                  <MdLockOutline aria-hidden="true" />
                </Avatar>
                <Typography variant="h6" fontWeight={600}>
                  Seguridad
                </Typography>
              </Stack>
              <Typography color="text.secondary">
                Actualizá tu contraseña para proteger el acceso a tu cuenta.
              </Typography>
              <Button
                variant="contained"
                onClick={() => navigate(ROUTES.CHANGE_PASSWORD)}
              >
                Cambiar contraseña
              </Button>
            </Stack>
          </Paper>
        </Grid>
      </Grid>
    </PageLayout>
  );
}
